function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
let txCounter = 0;
/**
 * Tracks the lifecycle of one optimistic mutation:
 *   pending → persisting → completed | failed
 *
 * `isPersisted` resolves when the mutation reaches `completed` and rejects
 * when it reaches `failed`. `rollback()` is invoked by the owning collection
 * (manually or automatically when the handler throws).
 */ export class Transaction {
    get state() {
        return this._state;
    }
    /** @internal moves to `persisting`; called by the collection. */ markPersisting() {
        if (this._state === 'pending') {
            this._state = 'persisting';
        }
    }
    /** @internal resolves the transaction successfully. */ complete() {
        if (this.settled) {
            return;
        }
        this.settled = true;
        this._state = 'completed';
        this.rollbackFn = null;
        this.resolveFn();
    }
    /** @internal fails the transaction (does not run rollback itself). */ fail(error) {
        if (this.settled) {
            return;
        }
        this.settled = true;
        this._state = 'failed';
        this.rollbackFn = null;
        this.rejectFn(error);
    }
    /**
   * Rolls back the optimistic change and fails the transaction. Safe to call
   * once; no-ops if already settled.
   */ rollback(error) {
        if (this.settled) {
            return;
        }
        const fn = this.rollbackFn;
        if (fn) {
            fn();
        }
        this.fail(error !== null && error !== void 0 ? error : new Error(`Transaction ${this.id} rolled back`));
    }
    constructor(rollbackFn){
        _define_property(this, "id", void 0);
        _define_property(this, "_state", 'pending');
        _define_property(this, "resolveFn", void 0);
        _define_property(this, "rejectFn", void 0);
        _define_property(this, "settled", false);
        /** The collection-supplied rollback action for this transaction. */ _define_property(this, "rollbackFn", void 0);
        _define_property(this, "isPersisted", void 0);
        this.id = `tx_${txCounter += 1}`;
        this.rollbackFn = rollbackFn;
        this.isPersisted = new Promise((resolve, reject)=>{
            this.resolveFn = resolve;
            // Swallow unhandled rejection if no one awaits isPersisted; the failure
            // is still observable via `state`.
            this.rejectFn = (error)=>{
                reject(error);
            };
        });
        // Prevent unhandledrejection noise when callers don't await isPersisted.
        this.isPersisted.catch(()=>undefined);
    }
}
